import { useEffect, useRef, useState, useCallback } from 'react'
import { Message } from '@cx/ui'

interface CapInstance {
  solve: () => Promise<{ token: string }>
  reset: () => void
  destroy?: () => void
  start?: (event?: Event) => Promise<string>
}

interface CapOptions {
  apiEndpoint: string
}

type CapConstructor = new (options: CapOptions, container?: HTMLElement | null) => CapInstance

interface UseCapCaptchaOptions {
  apiEndpoint?: string
  onSuccess?: (token: string) => void
  onError?: (error: Error) => void
}

interface UseCapCaptchaReturn {
  containerRef: React.RefObject<HTMLDivElement>
  start: (event?: React.MouseEvent) => Promise<string>
  reset: () => void
  isReady: boolean
  token: string | undefined
}

/**
 * go-cap 验证码 Hook
 * 使用 @cap.js/widget 与后端 go-cap 服务交互
 */
export const useCapCaptcha = (options: UseCapCaptchaOptions = {}): UseCapCaptchaReturn => {
  const { apiEndpoint = '/api/v1/public/captcha/', onSuccess, onError } = options
  const containerRef = useRef<HTMLDivElement>(null)
  const capRef = useRef<CapInstance | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [token, setToken] = useState<string | undefined>()

  useEffect(() => {
    const initCap = async () => {
      try {
        const CapModule = await import('@cap.js/widget')
        const Cap = (CapModule.default || CapModule) as unknown as CapConstructor

        const cap = new Cap(
          {
            apiEndpoint,
          },
          containerRef.current ?? undefined
        )

        const originalSolve = cap.solve.bind(cap)
        cap.start = async (_event?: Event): Promise<string> => {
          try {
            const result = await originalSolve()
            const resultToken = result.token
            setToken(resultToken)
            onSuccess?.(resultToken)
            return resultToken
          } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err))
            Message.error('验证失败，请重试')
            onError?.(error)
            throw error
          }
        }

        capRef.current = cap
        setIsReady(true)
      } catch (err) {
        console.error('[useCapCaptcha] Failed to initialize Cap.js:', err)
        Message.error('验证码组件加载失败')
      }
    }

    initCap()

    return () => {
      if (capRef.current) {
        if (typeof capRef.current.destroy === 'function') {
          capRef.current.destroy()
        }
        capRef.current = null
      }
    }
  }, [apiEndpoint, onSuccess, onError])

  const start = useCallback(
    async (event?: React.MouseEvent): Promise<string> => {
      const instance = capRef.current
      if (!instance || !instance.start) {
        return Promise.reject(new Error('Captcha not initialized'))
      }
      return instance.start(event?.nativeEvent)
    },
    []
  )

  const reset = useCallback(() => {
    capRef.current?.reset()
    setToken(undefined)
  }, [])

  return {
    containerRef,
    start,
    reset,
    isReady,
    token,
  }
}

export default useCapCaptcha
